import { CLIENT_ROLES, type ClientRole, type CompanyStatus, type Role, type SubscriptionType } from "./enums";
import { parseEmailAddress, type EmailAddress, type EmailAddressParseError } from "./email";
import {
  knownIdOrEmptyFromInput,
  knownIdOrNullFromInput,
  type KnownIdOption,
} from "./idInput";
import { ok, type Result } from "./result";
import { parseOneOf, type OneOfParseError } from "./guards";
import { cleanOptionalText, formatCountLabel } from "./text";

export type TeamUserDraft = {
  readonly email: string;
  readonly name: string;
  readonly role: ClientRole;
};

export const emptyTeamUserCreateFormState = (): TeamUserDraft => ({
  email: "",
  name: "",
  role: "clientReadOnly",
});

export type PreparedTeamUserCreate = {
  readonly email: EmailAddress;
  readonly name?: string;
  readonly role: ClientRole;
};

export type TeamUserPersistenceInput<CompanyId extends string = string> = PreparedTeamUserCreate & {
  readonly companyId: CompanyId;
};

export type PreparedTeamUserFields<CompanyId extends string = string> = {
  readonly companyId: CompanyId;
  readonly email: EmailAddress;
  readonly name: string | null;
  readonly username: null;
  readonly role: ClientRole;
  readonly active: true;
  readonly accountExpiresAt: null;
  readonly passwordExpiresAt: number;
};

export type PreparedTeamUserInsert<CompanyId extends string = string> = PreparedTeamUserFields<CompanyId> & {
  readonly clerkUserId: string;
  readonly createdAt: number;
};

export type TeamUserFormError = EmailAddressParseError;

export type TeamUserNotice =
  | { readonly kind: "success"; readonly text: string }
  | { readonly kind: "error"; readonly text: string };

export type TeamUserSubmissionState = {
  readonly busy: boolean;
  readonly notice: TeamUserNotice | null;
};

export const emptyTeamUserSubmissionState = (): TeamUserSubmissionState => ({
  busy: false,
  notice: null,
});

export type TeamUserSubmitAccess = {
  readonly entitled: boolean;
  readonly canManage: boolean;
  readonly prepared: boolean;
  readonly busy: boolean;
};

export type TeamUserFormAccess = Pick<TeamUserSubmitAccess, "entitled" | "canManage">;

export type TeamUserSubmitButtonView = {
  readonly disabled: boolean;
  readonly icon: "plus";
  readonly label: "Add user" | "Adding...";
};

export type TeamUserStatus = "active" | "disabled";
export type TeamPlanEntitlementState = "entitled" | "locked";
export type TeamPlanEntitlementIconName = "check" | "lock";

export type TeamUserStatusToggleInput<UserId extends string = string> = {
  readonly userId: UserId;
  readonly active: boolean;
  readonly entitled: boolean;
  readonly canManage: boolean;
};

export type TeamUserStatusToggleCommand<UserId extends string = string> =
  | { readonly kind: "ready"; readonly userId: UserId; readonly active: boolean }
  | { readonly kind: "blocked" };

export type TeamPlanDisplayInput = {
  readonly planName?: string | null;
  readonly companyStatus: CompanyStatus;
};

const TEAM_USER_STATUS_LABELS = {
  active: "Active",
  disabled: "Disabled",
} satisfies Record<TeamUserStatus, string>;

const TEAM_USER_STATUS_TONES = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  disabled: "bg-slate-50 text-slate-600 ring-slate-200",
} satisfies Record<TeamUserStatus, string>;

const TEAM_USER_STATUS_TOGGLE_LABELS = {
  active: "Disable",
  disabled: "Enable",
} satisfies Record<TeamUserStatus, string>;

const TEAM_PLAN_ENTITLEMENT_TONES = {
  entitled: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  locked: "bg-amber-50 text-amber-700 ring-amber-200",
} satisfies Record<TeamPlanEntitlementState, string>;

const TEAM_PLAN_ENTITLEMENT_ICONS = {
  entitled: "check",
  locked: "lock",
} satisfies Record<TeamPlanEntitlementState, TeamPlanEntitlementIconName>;

export const teamUserAddedNotice = (): TeamUserNotice => ({
  kind: "success",
  text: "Access record added. No invitation email has been sent.",
});

export const teamUserErrorNotice = (text: string): TeamUserNotice => ({
  kind: "error",
  text,
});

export type TeamUserSubmissionAction =
  | { readonly type: "started" }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextTeamUserSubmissionState = (
  _current: TeamUserSubmissionState,
  action: TeamUserSubmissionAction,
): TeamUserSubmissionState => {
  switch (action.type) {
    case "started":
      return { busy: true, notice: null };
    case "succeeded":
      return { busy: false, notice: teamUserAddedNotice() };
    case "failed":
      return { busy: false, notice: teamUserErrorNotice(action.error) };
  }
};

export const teamUserNoticeToneClass = (notice: TeamUserNotice): string =>
  notice.kind === "success" ? "text-emerald-700" : "text-red-600";

export const teamUserStatusForActive = (active: boolean): TeamUserStatus =>
  active ? "active" : "disabled";

export const teamUserStatusLabel = (status: TeamUserStatus): string =>
  TEAM_USER_STATUS_LABELS[status];

export const teamUserStatusToneClass = (status: TeamUserStatus): string =>
  TEAM_USER_STATUS_TONES[status];

export const teamUserStatusToggleLabel = (status: TeamUserStatus): string =>
  TEAM_USER_STATUS_TOGGLE_LABELS[status];

export const teamUserStatusToggleCommand = <UserId extends string>({
  userId,
  active,
  entitled,
  canManage,
}: TeamUserStatusToggleInput<UserId>): TeamUserStatusToggleCommand<UserId> =>
  entitled && canManage
    ? { kind: "ready", userId, active: !active }
    : { kind: "blocked" };

export const teamPlanEntitlementState = (entitled: boolean): TeamPlanEntitlementState =>
  entitled ? "entitled" : "locked";

export const teamPlanEntitlementToneClass = (state: TeamPlanEntitlementState): string =>
  TEAM_PLAN_ENTITLEMENT_TONES[state];

export const teamPlanEntitlementIconName = (state: TeamPlanEntitlementState): TeamPlanEntitlementIconName =>
  TEAM_PLAN_ENTITLEMENT_ICONS[state];

export const teamPlanEntitlementLabel = (
  state: TeamPlanEntitlementState,
  planName: string,
): string => state === "entitled" ? planName : "Premium required";

export const teamPlanDisplayName = ({
  planName,
  companyStatus,
}: TeamPlanDisplayInput): string =>
  planName ?? (companyStatus === "trialing" ? "Trial" : "No plan");

export const teamCompanyUserCountLabel = (count: number): string =>
  formatCountLabel(count, "company user");

export const canSubmitTeamUserCreate = ({
  entitled,
  canManage,
  prepared,
  busy,
}: TeamUserSubmitAccess): boolean => entitled && canManage && prepared && !busy;

export const teamUserFormControlsDisabled = ({
  entitled,
  canManage,
}: TeamUserFormAccess): boolean => !entitled || !canManage;

export const teamUserSubmitButtonView = ({
  canSubmit,
  busy,
}: {
  readonly canSubmit: boolean;
  readonly busy: boolean;
}): TeamUserSubmitButtonView => ({
  disabled: !canSubmit,
  icon: "plus",
  label: busy ? "Adding..." : "Add user",
});

export type TeamManagementCompany = {
  readonly status: CompanyStatus;
  readonly trialEndsAt: number | null;
};

export type TeamManagementPlan = {
  readonly tier: SubscriptionType;
};

export const CLIENT_ROLE_OPTIONS = [
  "clientReadOnly",
  "clientAdmin",
] as const satisfies readonly ClientRole[];

export const clientRoleDisplayName = (role: ClientRole): string => {
  const labels: Record<ClientRole, string> = {
    clientAdmin: "Client administrator",
    clientReadOnly: "Read only",
  };
  return labels[role];
};

export const teamUserFormErrorMessage = (error: TeamUserFormError): string => {
  switch (error.kind) {
    case "missingEmail":
      return "Email is required.";
    case "invalidEmail":
      return "Enter a valid email address.";
  }
};

export const hasActiveTeamManagementTrial = (
  company: TeamManagementCompany,
  now: number,
): boolean =>
  company.status === "trialing" &&
  (company.trialEndsAt === null || company.trialEndsAt > now);

export const hasTeamManagementEntitlement = (
  company: TeamManagementCompany,
  plan: TeamManagementPlan | null,
  now: number,
  staging = false,
): boolean =>
  staging || hasActiveTeamManagementTrial(company, now) || plan?.tier === "premium";

export const clientRoleInputResult = (value: string): Result<ClientRole, OneOfParseError> =>
  parseOneOf(CLIENT_ROLES, value);

export const clientRoleFromInput = (value: string, fallback: ClientRole): ClientRole => {
  const parsed = clientRoleInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export type TeamUserCreateFormAction =
  | { readonly type: "emailInput"; readonly value: string }
  | { readonly type: "nameInput"; readonly value: string }
  | { readonly type: "roleInput"; readonly value: string };

export const nextTeamUserCreateFormState = (
  current: TeamUserDraft,
  action: TeamUserCreateFormAction,
): TeamUserDraft => {
  switch (action.type) {
    case "emailInput":
      return { ...current, email: action.value };
    case "nameInput":
      return { ...current, name: action.value };
    case "roleInput":
      return { ...current, role: clientRoleFromInput(action.value, current.role) };
  }
};

export const teamUserCreateFormSubmittedState = (): TeamUserDraft =>
  emptyTeamUserCreateFormState();

export const clientRoleForDisplay = (role: Role): ClientRole => {
  const parsed = clientRoleInputResult(role);
  return parsed.ok ? parsed.value : "clientReadOnly";
};

export type TeamMemberOption<UserId extends string> = KnownIdOption<UserId>;

export const teamUserIdOrEmptyFromInput = <UserId extends string>(
  team: readonly TeamMemberOption<UserId>[],
  value: string,
  fallback: UserId | "",
): UserId | "" =>
  knownIdOrEmptyFromInput(team, value, fallback);

export const teamUserIdOrNullFromInput = <UserId extends string>(
  team: readonly TeamMemberOption<UserId>[],
  value: string,
  fallback: UserId | null,
): UserId | null =>
  knownIdOrNullFromInput(team, value, fallback);

export const isTeamUserDraftTouched = (draft: TeamUserDraft): boolean =>
  draft.email.trim() !== "" || draft.name.trim() !== "";

export const prepareTeamUserCreate = (
  draft: TeamUserDraft,
): Result<PreparedTeamUserCreate, TeamUserFormError> => {
  const email = parseEmailAddress(draft.email);
  if (!email.ok) return email;

  const name = cleanOptionalText(draft.name);
  return ok({
    email: email.value,
    ...(name === undefined ? {} : { name }),
    role: draft.role,
  });
};

export type TeamUserCreateFormInput = {
  readonly draft: TeamUserDraft;
  readonly entitled: boolean;
  readonly canManage: boolean;
  readonly busy: boolean;
};

export type TeamUserCreateFormView = {
  readonly prepared: Result<PreparedTeamUserCreate, TeamUserFormError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
  readonly controlsDisabled: boolean;
  readonly submitButton: TeamUserSubmitButtonView;
};

export const teamUserCreateFormView = ({
  draft,
  entitled,
  canManage,
  busy,
}: TeamUserCreateFormInput): TeamUserCreateFormView => {
  const prepared = prepareTeamUserCreate(draft);
  const touched = isTeamUserDraftTouched(draft);
  const canSubmit = canSubmitTeamUserCreate({
    entitled,
    canManage,
    prepared: prepared.ok,
    busy,
  });
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? teamUserFormErrorMessage(prepared.error) : null,
    canSubmit,
    controlsDisabled: teamUserFormControlsDisabled({ entitled, canManage }),
    submitButton: teamUserSubmitButtonView({ canSubmit, busy }),
  };
};

export const manualTeamClerkUserId = (companyId: string, now: number): string =>
  `manual:${companyId}:${now.toString(36)}`;

export const prepareTeamUserFields = <CompanyId extends string>(
  input: TeamUserPersistenceInput<CompanyId>,
  now: number,
): PreparedTeamUserFields<CompanyId> => ({
  companyId: input.companyId,
  email: input.email,
  name: input.name ?? null,
  username: null,
  role: input.role,
  active: true,
  accountExpiresAt: null,
  passwordExpiresAt: now,
});

export const prepareTeamUserInsert = <CompanyId extends string>(
  input: TeamUserPersistenceInput<CompanyId>,
  now: number,
): PreparedTeamUserInsert<CompanyId> => ({
  clerkUserId: manualTeamClerkUserId(input.companyId, now),
  ...prepareTeamUserFields(input, now),
  createdAt: now,
});

